import React, { useMemo, useState } from "react";
import { Pagination as PaginationType } from "@sscs/types";
import { DataTable, DataTableColumn, Page, useSort } from "@sscs/ui";
import { compareValues, getPageItems } from "@sscs/utils";
import { PERMISSION } from "Constants";
import { useAllowedTo, useCheckPaginationPage } from "Hooks";
import { Package, Project } from "Types";
import PackageRow from "./PackageRow";

const initPagination: PaginationType = {
  currentPage: 1,
  itemsPerPage: 10,
};

type extraCasesType = {
  [key in keyof Package]?: (
    first: Package,
    second: Package,
    reverse: boolean,
  ) => number;
};

const specialCasesForSorting: extraCasesType = {
  last_scan: (first, second, reverse) =>
    compareValues(first.last_scan || "", second.last_scan || "", reverse),
};

type PackagesListProps = {
  project: Project;
};

const PackagesList = ({ project }: PackagesListProps): React.JSX.Element => {
  const { packages } = project;
  const [pagination, setPagination] = useState(initPagination);
  const [sortBy, setSortBy, sortedPackages] = useSort<Package, keyof Package>(
    packages,
    "last_scan",
    true,
    specialCasesForSorting,
  );
  const packagesList = useMemo(() => {
    return getPageItems(
      sortedPackages,
      pagination.currentPage,
      pagination.itemsPerPage,
    );
  }, [sortedPackages, pagination]);

  useCheckPaginationPage({
    matchCount: packages.length,
    itemLength: packagesList.length,
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    handlePaginationChange: setPagination,
  });

  const canCRUDPackage = useAllowedTo({
    permissions: [PERMISSION.CREATE_PACKAGE],
  });

  const columns: DataTableColumn<keyof Package>[] = [
    { id: "status", label: "Status", width: "90px" },
    { id: "approval", label: "Approval", width: "150px" },
    {
      id: "name",
      label: "Package",
      sortProperty: "name",
      sortId: "sort-package",
      width: "100%",
    },
    { id: "safe", label: "SAFE assessment", width: "400px" },
    {
      id: "version_count",
      label: "# Versions",
      sortProperty: "version_count",
      sortId: "sort-versions",
      width: "130px",
    },
    {
      id: "last_scan",
      label: "Last scan",
      sortProperty: "last_scan",
      sortId: "sort-last-scan",
      startReverse: true,
      width: "150px",
    },
    {
      id: "actions",
      align: "center",
      width: "55px",
      hidden: !canCRUDPackage,
    },
  ];

  return (
    <>
      <Page.Body>
        <DataTable
          chrome="plain"
          title="Packages"
          size="large"
          columns={columns}
          sort={sortBy}
          onSortChange={setSortBy}
          empty={{ subject: "packages" }}
          pagination={{
            id: "packagtes-pagination",
            itemCount: project.packages.length,
            pagination,
            onChange: setPagination,
          }}
        >
          {packagesList.map((item, index) => (
            <PackageRow
              rowIndex={index}
              key={item.package_id}
              packageInfo={item}
              projectName={project.name}
            />
          ))}
        </DataTable>
      </Page.Body>
    </>
  );
};

export default PackagesList;
